import { EDevice, LOCAL_STORAGE_KEYS, QUERY_PARAMS } from '@/constants';
import type { Cart } from '@/modules/cart/domain/Cart';
import type { InterfaceInteraction } from '@/modules/perseo-billing/interfaces';
import type {
  PaymentIntentWebpayError,
  PaymentIntentWebpayResponse
} from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/orderV2';
import type { ISinglePayment } from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/payment-methods.interfaceV2';
import { PaymentProcessor } from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/payment-methods.interfaceV2';
import {
  isDeviceAndroid,
  isDeviceIos
} from '@/modules/perseo-billing/utils/index';
import { priceDtoToPriceString } from '@/modules/shared/mappers/priceDtoToPriceString';

const PAYMENT_ERROR_NAME = 'CencoPaymentError';
const deviceOS = ['android', 'ios'];

type TDeviceOS = (typeof deviceOS)[number];

const buildPurchaseSearchParams = (
  orderId: string | undefined
): URLSearchParams => {
  const search = new URLSearchParams(window.location.search);
  search.set('orderId', orderId ?? '');

  if (isDeviceAndroid()) {
    search.set(QUERY_PARAMS.DEVICE, EDevice.ANDROID);
  }

  if (isDeviceIos()) {
    search.set(QUERY_PARAMS.DEVICE, EDevice.IOS);
  }

  const hasDevice = search.get(QUERY_PARAMS.DEVICE);
  if (hasDevice) return search;

  const storageDevice = getDeviceLocalStorage();
  const isAndroid = storageDevice === EDevice.ANDROID;
  const isIos = storageDevice === EDevice.IOS;
  const isKiosco = storageDevice === EDevice.KIOSCO;
  if (isAndroid || isIos || isKiosco) {
    search.set(QUERY_PARAMS.DEVICE, storageDevice);
  }
  return search;
};

const getDeviceLocalStorage = () => {
  const device = localStorage.getItem(LOCAL_STORAGE_KEYS.DEVICE);
  if (!device) return '';
  if (device === 'web') return '';
  return device;
};

function detectDevice(): TDeviceOS | void {
  const userAgent = navigator.userAgent || window.opera || '';

  if (/android/i.test(userAgent)) {
    return EDevice.ANDROID;
  }

  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return EDevice.IOS;
  }
}

function getCookie(input: string) {
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    const name = cookies[i].split('=')[0].trim();
    const value = cookies[i].split('=')[1].trim();

    if (name === input) {
      return value;
    } else if (value === input) {
      return name;
    }
  }
  return '';
}

const formatPrice = (price: number) => {
  if (price === 0) return '';

  return priceDtoToPriceString({
    centAmount: price,
    currencyCode: 'CLP'
  });
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const isWebpayPayment = (paymentMethods: ISinglePayment[]) =>
  paymentMethods.some(
    (payment) => payment?.paymentProcessor === PaymentProcessor?.WEBPAY
  );

const isCencoPayPayment = (paymentMethods: ISinglePayment[]) =>
  paymentMethods.some(
    (payment) => payment?.paymentProcessor === PaymentProcessor?.CENCOPAY
  );

const isKioscoPayment = (paymentMethods: ISinglePayment[]) =>
  paymentMethods?.some(
    (payment) => payment?.paymentProcessor === PaymentProcessor?.PINPAD
  );

const isPaymentIntentWebpayError = (
  response: unknown
): response is PaymentIntentWebpayError => {
  return Boolean(
    response &&
      typeof response === 'object' &&
      'data' in response &&
      'errorCode' in response &&
      'name' in response
  );
};

const isPaymentIntentWebpaySuccess = (
  response: unknown
): response is PaymentIntentWebpayResponse => {
  return Boolean(
    response &&
      typeof response === 'object' &&
      'token' in response &&
      'url' in response &&
      'redirectTo' in response
  );
};

const hasWebpayCencoPaymentError = (interactions: InterfaceInteraction[]) => {
  return interactions?.some(
    (interaction) =>
      isPaymentIntentWebpayError(interaction?.processorResponse) &&
      interaction?.processorResponse?.name === PAYMENT_ERROR_NAME
  );
};

export const getPaymentsInteractionsOfOrder = (
  cart: Cart,
  orderId: string
): InterfaceInteraction[] => {
  const payments = cart?.payments?.find(
    (payment) =>
      payment.interfaceInteractions && payment.interfaceInteractions.length > 0
  );

  const interfaceInteractions = payments?.interfaceInteractions;
  if (!interfaceInteractions) return [];

  const interactionId = interfaceInteractions.find(
    (interaction) => interaction.tentativeOrderNumber === orderId
  )?.interactionId;

  if (!interactionId) return [];

  const filteredInteractions = interfaceInteractions.filter(
    (interaction) => interaction.interactionId === interactionId
  );

  return filteredInteractions;
};

export const getWebpayTokenFromActiveCartPayments = (
  cart: Cart,
  orderNumber: string
) => {
  const interactions = getPaymentsInteractionsOfOrder(cart, orderNumber);
  const hasInteraction = interactions?.length > 0;

  if (!hasInteraction) {
    console.error(
      `No payment interaction found for order number: ${orderNumber}`
    );
    return;
  }

  const hasError = hasWebpayCencoPaymentError(interactions ?? []);
  if (hasError) {
    console.error(
      `Payment processing failed due to CencoPaymentError for order: ${orderNumber}`
    );
    return;
  }

  const successfulInteraction = interactions?.find(
    (interaction) =>
      interaction?.processorResponse &&
      isPaymentIntentWebpaySuccess(interaction?.processorResponse)
  );

  const token =
    successfulInteraction?.processorResponse &&
    isPaymentIntentWebpaySuccess(successfulInteraction?.processorResponse)
      ? successfulInteraction?.processorResponse?.token
      : successfulInteraction?.token;

  return token;
};

export {
  buildPurchaseSearchParams,
  delay,
  detectDevice,
  formatPrice,
  getCookie,
  getDeviceLocalStorage,
  hasWebpayCencoPaymentError,
  isCencoPayPayment,
  isKioscoPayment,
  isPaymentIntentWebpayError,
  isWebpayPayment
};
